import _ from "lodash";
import ol from "openlayers";
import { InventoryFormdataItem } from "../hooks/database/types/inventories";
import { InventoryObject } from "../hooks/database/useCurrentObject";
import { PouchDocument } from "../hooks/database/common";

export interface Style {
  color: string;
  borderColor: string;
  form: string;
  formField: string;
  formFieldValue: string;
}

const mapUtils = {
  getInventoryLayers: function (map: ol.Map) {
    return map
      .getLayers()
      .getArray()
      .filter((layer) => {
        const name = layer.get("name");
        return name !== undefined && name.startsWith("Inventory");
      }) as ol.layer.Layer[];
  },
  getMapCenter: function (map: ol.Map) {
    let view = map.getView();
    return view.getCenter();
  },
  getFeatureExtentCenter: function (feature: ol.Feature): ol.Coordinate {
    const ext = feature.getGeometry().getExtent();
    const x = (ext[0] + ext[2]) / 2.0;
    const y = (ext[1] + ext[3]) / 2.0;
    return [x, y];
  },
  featureToGeojson: function (feature: ol.Feature, projection: ol.ProjectionLike) {
    const geoJsonFormat = new ol.format.GeoJSON();
    const geoJSON = geoJsonFormat.writeFeature(feature, {
      featureProjection: projection,
      dataProjection: projection,
    });
    return JSON.parse(geoJSON);
  },
  zoomToExtent: function (map: ol.Map, extent: ol.Extent) {
    let view = map.getView();
    let mapSize = map.getSize();
    map.getView().fit(extent, { size: mapSize });
    const currentZoom = view.getZoom();
    if (currentZoom > 18) {
      view.setZoom(18);
    }
  },
  getCurrentZoom: function (map: ol.Map) {
    let view = map.getView();
    return Math.round(view.getZoom());
  },
  createExtentFromPixel: function (map: ol.Map, pixel: ol.Pixel): ol.Extent {
    const buffer = 25;
    const pixelX = pixel[0];
    const pixelY = pixel[1];
    const pixelBottomLeft: ol.Pixel = [pixelX - buffer, pixelY + buffer];
    const pixelTopRight: ol.Pixel = [pixelX + buffer, pixelY - buffer];
    const coordinateBottomLeft = map.getCoordinateFromPixel(pixelBottomLeft);
    const coordinateTopRight = map.getCoordinateFromPixel(pixelTopRight);
    return [coordinateBottomLeft[0], coordinateBottomLeft[1], coordinateTopRight[0], coordinateTopRight[1]];
  },
  createLineString: function (fromPoint: ol.Coordinate, toPoint: ol.Coordinate) {
    const coordinates = [fromPoint, toPoint];
    return new ol.geom.LineString(coordinates, "XY");
  },
  rotateCoordinates: function (inCoords: ol.Coordinate[], centerPoint: ol.Coordinate, angle: number): ol.Coordinate[] {
    return inCoords.map(([x, y]) => {
      const currentRotation = mapUtils.getRotation(x - centerPoint[0], y - centerPoint[1]);
      const newRotation = currentRotation + angle * (Math.PI / 180);
      const length = Math.sqrt(Math.pow(x - centerPoint[0], 2) + Math.pow(y - centerPoint[1], 2));
      return [centerPoint[0] + length * Math.cos(newRotation), centerPoint[1] + length * Math.sin(newRotation)];
    });
  },
  rotateArrayCoordinates: function (inCoords: ol.Coordinate[][], centerPoint: ol.Coordinate, angle: number) {
    return inCoords.map((coords) => mapUtils.rotateCoordinates(coords, centerPoint, angle));
  },
  getRotation: function (dx: number, dy: number) {
    let rot = 0;
    if (dx === 0 && dy === 0) {
      return rot;
    }
    if (dy === 0) {
      rot = dx > 0 ? 0 : Math.PI;
    } else if (dx === 0) {
      rot = dy > 0 ? 0.5 * Math.PI : 1.5 * Math.PI;
    } else {
      rot = Math.atan(dy / dx);
      if (dx < 0 && dy > 0) {
        rot += Math.PI;
      }
      if (dx < 0 && dy < 0) {
        rot += Math.PI;
      }
      if (dx > 0 && dy < 0) {
        rot += 2 * Math.PI;
      }
    }
    return rot;
  },
  createSelectInteraction: function () {
    const fillColor = "rgba(255,255,255, 0.9)";
    const strokeColor = "rgba(224, 60, 49, 1)";
    const selectedStyle = new ol.style.Style({
      image: new ol.style.Circle({
        radius: 8,
        fill: new ol.style.Fill({
          color: fillColor,
        }),
        stroke: new ol.style.Stroke({
          color: strokeColor,
          width: 2,
        }),
      }),
      fill: new ol.style.Fill({
        color: fillColor,
      }),
      stroke: new ol.style.Stroke({
        color: "rgba(49, 224, 49, 0.7)",
        width: 4,
      }),
    });
    return new ol.interaction.Select({
      condition: ol.events.condition.click,
      style: selectedStyle,
    });
  },
  panAndZoom: function (map: ol.Map, coordinate: ol.Coordinate) {
    let view = map.getView();
    view.animate({
      duration: 300,
      resolution: map.getView().getResolution(),
      center: view.getCenter(),
    });
    view.setCenter(coordinate);
    map.getView().setResolution(map.getView().getResolution() * 0.2);
  },
  panToCoord: function (map: ol.Map, coordinate: ol.Coordinate) {
    let view = map.getView();
    view.animate({
      duration: 300,
      center: view.getCenter(),
    });
    view.setCenter(coordinate);
  },
  getFeaturesFromPixel: function (map: ol.Map, pixel: [number, number]) {
    const extent = mapUtils.createExtentFromPixel(map, pixel);
    const inventoryLayers = mapUtils.getInventoryLayers(map);
    const featuresFound: ol.Feature[] = [];
    _.forEach(inventoryLayers, (inventoryLayer) => {
      const source = inventoryLayer.getSource() as ol.source.Vector;
      source.forEachFeatureIntersectingExtent(extent, (feature) => {
        featuresFound.push(feature);
      });
    });
    return featuresFound;
  },
  getResolution: function (map: ol.Map) {
    const view = map.getView();
    return view.getResolution();
  },
  deselectedStyleCache: {} as Record<number, ol.style.Style[]>,
  deselectedPointStyleCache: {},
  deselectedLineStringStyleCache: {},
  deselectedPolygonStyleCache: {},
  getDeselectedStyle: function (feature: ol.Feature) {
    let size = 1;
    if (feature && feature.get && feature.get("features")) {
      size = feature.get("features").length;
    }
    let style = mapUtils.deselectedStyleCache[size];
    if (!style) {
      const color = "0,0,0";
      const radius = Math.max(8, Math.min(size * 0.75, 20));
      const dashSize = (2 * Math.PI * radius) / 6;
      const strokeWidth = size > 1 ? 15 : 5;
      const dash = size > 1 ? [0, dashSize, dashSize, dashSize, dashSize, dashSize, dashSize] : [];
      style = mapUtils.deselectedStyleCache[size] = [
        new ol.style.Style({
          image: new ol.style.Circle({
            radius: radius,
            stroke: new ol.style.Stroke({
              color: "rgba(" + color + ",0.2)",
              width: strokeWidth,
              lineDash: dash,
            }),
            fill: new ol.style.Fill({ color: "rgba(" + color + ",0.1)" }),
          }),
          fill: new ol.style.Fill({
            color: "rgba(" + color + ",0.1)",
          }),
          stroke: new ol.style.Stroke({
            color: "rgba(" + color + ",0.2)",
            width: 2,
          }),
          text: new ol.style.Text({
            text: size > 1 ? size.toString() : "",
            fill: new ol.style.Fill({
              color: "#fff",
            }),
          }),
        }),
      ];
    }
    return style;
  },
  styleCache: {} as Record<string, ol.style.Style[]>,
  getFormStyleMapping: function (style: Record<string, Style> | null, feature: InventoryObject) {
    let computedStyle = {
      fill: "white",
      stroke: "lightgrey",
      id: "",
      label: "",
      styleable: false,
    };
    const formData = _.cloneDeep(feature.data);
    if (!formData) {
      return null;
    }
    const mappingList: {
      data: PouchDocument<InventoryFormdataItem>;
      rgb: {
        fill: string;
        stroke: string;
        id: string;
        label: string;
        styleable: boolean;
      };
    }[] = [];
    let lastElementFound = false;
    let lastElement = false;
    _.forEach(formData.reverse(), (data) => {
      computedStyle = {
        fill: "white",
        stroke: "lightgrey",
        id: "",
        label: "",
        styleable: false,
      };
      if (data && data.values) {
        _.forEach(data.values, (entry) => {
          const styleKey = data.form + "_" + entry.key + "_" + entry.value;
          if (style && style[styleKey]) {
            if (!lastElementFound) {
              computedStyle.fill = style[styleKey].color;
              computedStyle.stroke = style[styleKey].borderColor;
              computedStyle.id = computedStyle.fill + computedStyle.stroke;
              computedStyle.label = entry.value as string;
              computedStyle.styleable = true;
              if (sessionStorage.getItem("formListCurrentStyling") && sessionStorage.getItem("formListCurrentStyling") === "false") {
                lastElementFound = sessionStorage.getItem("formListTempStyling") !== "true";
              } else {
                lastElementFound = sessionStorage.getItem("formListTempStyling") === "true";
              }
            } else {
              computedStyle.fill = "white";
              computedStyle.stroke = "black";
              computedStyle.id = computedStyle.fill + computedStyle.stroke;
              computedStyle.label = entry.value as string;
              computedStyle.styleable = true;
            }
          }
          if (sessionStorage.getItem("formListDefaultStyling") && sessionStorage.getItem("formListDefaultStyling") === "false") {
            computedStyle.fill = "white";
            computedStyle.stroke = "black";
            computedStyle.id = computedStyle.fill + computedStyle.stroke;
            computedStyle.label = entry.value as string;
            computedStyle.styleable = true;
          } else {
            //Row can't be empty
          }

          let formFieldValue;
          const formName = data;
          if (entry && entry.value === "") {
            formFieldValue = entry;
          }
          const savedStylableFormNames = [];
          if (formFieldValue && formName) {
            savedStylableFormNames.push(formName);
          }
          let formStylable: PouchDocument<InventoryFormdataItem>;
          let formField;
          if (style) {
            formField = Object.values(style).filter((el) => {
              formStylable = data;
              return el.form === data.form && el.formField === entry.key && el.formFieldValue === "";
            });
          }
          if (formField && formField.length > 0) {
            const match = savedStylableFormNames.filter((el) => {
              return el._id === formStylable._id;
            });
            if (match.length === 0) {
              if (sessionStorage.getItem("formListDefaultStyling") && sessionStorage.getItem("formListDefaultStyling") === "false") {
                if (style && style[styleKey]) {
                  if (!lastElement) {
                    computedStyle.fill = style[styleKey].color;
                    computedStyle.stroke = style[styleKey].borderColor;
                    computedStyle.id = computedStyle.fill + computedStyle.stroke;
                    computedStyle.label = entry.value as string;
                    computedStyle.styleable = true;
                    if (sessionStorage.getItem("formListCurrentStyling") && sessionStorage.getItem("formListCurrentStyling") === "false") {
                      lastElementFound = sessionStorage.getItem("formListTempStyling") !== "true";
                    } else {
                      lastElementFound = sessionStorage.getItem("formListTempStyling") === "true";
                    }
                  } else if (lastElement) {
                    computedStyle.fill = "white";
                    computedStyle.stroke = "black";
                    computedStyle.id = computedStyle.fill + computedStyle.stroke;
                    computedStyle.label = entry.value as string;
                    computedStyle.styleable = true;
                  }
                }
              } else {
                //Row can't be empty
              }
            }
          }
        });
        mappingList.push({
          data: data,
          rgb: computedStyle,
        });
      }
    });
    return mappingList;
  },
  getComputedStyle: function (style: Record<string, { color: string; borderColor: string }> | null, features: ol.Feature[]) {
    const computedStyle = {
      fill: "rgba(224, 60, 49, 0.7)",
      stroke: "rgba(224, 60, 49, 0.7)",
      id: "",
      label: "",
    };
    if (features.length > 1 || !style) {
      return computedStyle;
    }
    const formData = features[0].get("formdata") as InventoryFormdataItem[];
    formData.forEach((data) => {
      if (data && data.values) {
        for (const entry of Object.values(data.values)) {
          const styleKey = data.form + "_" + entry.key + "_" + entry.value;
          if (style[styleKey]) {
            computedStyle.fill = style[styleKey].color;
            computedStyle.stroke = style[styleKey].borderColor;
            computedStyle.id = computedStyle.fill + computedStyle.stroke;
            computedStyle.label = entry.value as string;
            break;
          }
        }
      }
    });
    return computedStyle;
  },
  inventoryStyle: null as null | Record<string, Style>,
  getStyle: function (inventoryStyle?: Record<string, Style>, geomType?: "LineString" | string): ol.StyleFunction {
    if (inventoryStyle) {
      mapUtils.inventoryStyle = _.clone(inventoryStyle);
    }
    return (feature: ol.Feature | ol.render.Feature) => {
      let features = feature.get("features");
      if (!features) {
        features = [feature];
      }
      let size = 1;
      if (feature && feature.get && feature.get("features")) {
        size = feature.get("features").length;
      }
      const colors = mapUtils.getComputedStyle(mapUtils.inventoryStyle!, features);
      const styleId = size + colors.id;
      let style = mapUtils.styleCache[styleId];
      if (!style) {
        const radius = Math.max(8, Math.min(size * 0.75, 12));
        const dashSize = (2 * Math.PI * radius) / 6;
        const strokeWidth = size > 1 ? 10 : 3;
        const dash = size > 1 ? [0, dashSize, dashSize, dashSize, dashSize, dashSize, dashSize] : [];
        // Use provided colors or default to computed styles
        const strokeColor = inventoryStyle?.strokeColor?.color || colors.stroke;
        const fillColor = inventoryStyle?.fillColor?.color || colors.fill;
        style = [
          new ol.style.Style({
            image: new ol.style.Circle({
              radius: radius,
              stroke: new ol.style.Stroke({
                color: strokeColor,
                width: strokeWidth,
                lineDash: dash,
              }),
              fill: new ol.style.Fill({ color: fillColor }),
            }),
            fill: new ol.style.Fill({
              color: fillColor,
            }),
            stroke: new ol.style.Stroke({
              color: geomType === "LineString" ? fillColor : strokeColor,
              width: 4,
            }),
            text: new ol.style.Text({
              text: size > 1 ? size.toString() : features[0].get("title"),
              offsetY: size > 1 ? 0 : 16,
              fill: new ol.style.Fill({
                color: "#fff",
              }),
              stroke: new ol.style.Stroke({
                color: "#000",
                width: 4,
              }),
            }),
          }),
        ];
      }
      return style;
    };
  },
  getProjection: function (map: ol.Map) {
    return map.getView().getProjection();
  },
  getGeometryType: function (inventoryLayer: ol.layer.Layer) {
    const lyrName = inventoryLayer.get("name");
    if (lyrName.indexOf("Polyline") > 0) {
      return "LineString";
    } else if (lyrName.indexOf("Polygon") > 0) {
      return "Polygon";
    }
    return "Point";
  },
  getExtentFromSources: (sources: ol.source.Vector[]) => {
    return sources.reduce((extent, source) => ol.extent.extend(extent, source.getExtent()), ol.extent.createEmpty());
  },
};
export default mapUtils;
